import React, { useState } from "react";
import {
  Add,
  Remove,
  ExpandMore,
  ExpandLess,
  FitnessCenter,
  TrackChanges,
  Psychology,
  SportsScore,
  Timer,
  CheckCircle,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import "../pages/Exerciseadd/Exercise.css";
import { ip_back } from "../config";
import DurationMinutesSecondsInput from "./DurationMinutesSecondsInput";
import { formatDurationLabel, parseDurationMinutes } from "../utils/trainingDuration";
import { normalizeAppLang } from "../utils/appLanguages";
import { localizedExerciseName, localizedField } from "../utils/localizedCatalogFields";
import { competencyBlockIconSrc } from "../utils/competencyBlockIcon";
import { ExerciseTypeMark } from "./ExerciseTypeMark";

// Backend base URL for images (same as API: 3030 dev/remote, or REACT_APP_BACKEND_URL/PORT)
const getBaseUrl = () => ip_back;

const LEVEL_COLORS = [
  "#4caf50", "#8bc34a", "#cddc39", "#ffc107", "#ff9800",
  "#ff5722", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
];

const CardioIndicator = ({ level }) => {
  if (!level && level !== 0) return null;
  const maxLevel = 5;
  const filled = Math.min(level, maxLevel);
  return (
    <div className="excard-cardio">
      {Array.from({ length: maxLevel }, (_, i) => (
        <span
          key={i}
          className={`excard-cardio__dot ${i < filled ? "excard-cardio__dot--filled" : ""}`}
          style={i < filled ? { backgroundColor: LEVEL_COLORS[Math.min(i, LEVEL_COLORS.length - 1)] } : {}}
        />
      ))}
    </div>
  );
};

const getLocalized = (obj, keyPrefix, lang) => localizedField(obj, keyPrefix, lang);

const ThemeBadge = ({ theme, lng, highlighted = false }) => {
  const name = getLocalized(theme, "theme_name", lng) || (theme && theme.name) || "—";
  const level = theme?.level ?? theme?.the_lavel ?? 0;
  const color = LEVEL_COLORS[Math.min(Math.max(0, level - 1), LEVEL_COLORS.length - 1)] || "#90a4ae";
  return (
    <span
      className={`excard-tag excard-tag--theme${highlighted ? " excard-badge--theme-highlight" : ""}`}
      style={{
        backgroundColor: `${color}22`,
        borderColor: color,
        color: color,
        borderWidth: 1,
        borderStyle: "solid",
      }}
      title={level ? `L${level}` : undefined}
    >
      {name}
      {level ? ` · L${level}` : ""}
    </span>
  );
};

/**
 * Shared exercise card used in: Exerciseslist (add flow), Change exercise, Template apply wizard, Recommend dialogs.
 * selectMode: 'multi' = toggle selection + duration input (add flow). 'single' = click to select, no duration.
 */
export const ExerciseCard = ({
  exercise,
  isSelected,
  onToggle,
  onSelect,
  onDurationChange,
  lng,
  t,
  selectMode = "multi",
  showDurationInput = true,
  slotCbName = null,
  highlightThemeId = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t: tFn, i18n } = useTranslation();
  const tr = t || tFn;
  const lang = normalizeAppLang(
    lng || i18n?.language || localStorage.getItem("i18nextLng")
  );

  const name = localizedExerciseName(exercise, lang) || "—";
  const process =
    getLocalized(exercise, "ex_process", lang) ||
    exercise.ex_process ||
    "";
  const objective =
    getLocalized(exercise, "ex_objective", lang) ||
    exercise.ex_objective ||
    "";
  const instructionRaw =
    getLocalized(exercise, "ex_instruction", lang) ||
    exercise.ex_instruction ||
    "";
  const instruction = (instructionRaw != null && typeof instructionRaw === "string") ? instructionRaw.trim() : "";
  const hasInstruction = instruction.length > 0;
  // Support both list shape (type_ex, sub_type_ex) and getExerciseDetails shape (type_details, sub_type_details)
  const typeSource = exercise.type_ex || exercise.type_details;
  const subTypeSource = exercise.sub_type_ex || exercise.sub_type_details;
  const hasCbLink = !!exercise.cb_details || !!slotCbName;
  // In slot context (slotCbName set), always show the slot's block name so the card matches the slot (user language)
  const cbName = slotCbName || getLocalized(exercise.cb_details, "cb_name", lang) || "";
  // Label for type "Blocks of competency" in user's language
  const BC_TYPE_NAMES = { en: "Blocks of competency", fr: "Blocs de compétences", de: "Kompetenzblöcke", es: "Bloques de competencia" };
  const blocksOfCompetencyTypeName = BC_TYPE_NAMES[lang] || BC_TYPE_NAMES.en;
  // Type and sub-type: use current UI language (lang), fallback to any available locale
  const typeName = hasCbLink && cbName
    ? blocksOfCompetencyTypeName
    : (getLocalized(typeSource, "type_name", lang) || exercise.type || "");
  const subTypeName = hasCbLink && cbName
    ? cbName
    : (getLocalized(subTypeSource, "sub_type_name", lang) ||
       (exercise.competency_blocks?.length ? exercise.competency_blocks.map((b) => (typeof b === "string" ? b : getLocalized(b, "cb_name", lang) || b?.name)).filter(Boolean).join(", ") : "") ||
       "");
  const themes = exercise.themes || [];
  const skills = exercise.skills || [];
  const focusList = exercise.focus || [];
  const cardioLev = exercise.ex_cardio_lev;
  const duration = exercise.duration != null ? exercise.duration : (exercise.te_duration != null ? Number(exercise.te_duration) : 10);
  const subTypeImage = subTypeSource?.sub_type_image_normal;
  const blockIcon =
    competencyBlockIconSrc(exercise.cb_details) ||
    competencyBlockIconSrc(slotCbName) ||
    competencyBlockIconSrc(cbName) ||
    null;

  const isSingle = selectMode === "single";

  const handleClick = (e) => {
    if (e.target.tagName === "INPUT" || e.target.closest("input")) return;
    if (isSingle && onSelect) onSelect(exercise);
    else if (!isSingle && onToggle) onToggle(exercise);
  };

  return (
    <div
      className={`excard ${isSelected ? "excard--selected" : ""}`}
      onClick={handleClick}
      role={isSingle ? "button" : undefined}
      tabIndex={isSingle ? 0 : undefined}
      onKeyDown={
        isSingle && onSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(exercise);
              }
            }
          : undefined
      }
    >
      <div className="excard-header">
        <div className="excard-header__left">
          {(blockIcon || subTypeImage) && (
            <div
              className={`excard-header__img${
                blockIcon ? " excard-header__img--mark" : ""
              }`}
            >
              {blockIcon ? (
                <ExerciseTypeMark src={blockIcon} />
              ) : (
                <img src={getBaseUrl() + subTypeImage} alt="" />
              )}
            </div>
          )}
          <div className="excard-header__info">
            <h3 className="excard-header__name">{name}</h3>
            {process && <p className="excard-header__desc">{process}</p>}
            <div className="excard-header__type-row">
              {typeName && (
                <span className="excard-tag excard-tag--type">{typeName}</span>
              )}
              {subTypeName && (
                <span className="excard-tag excard-tag--subtype">
                  {subTypeName}
                </span>
              )}
              {cardioLev != null && (
                <span className="excard-tag excard-tag--cardio" title={tr("cardioLevel")}>
                  <FitnessCenter style={{ fontSize: 13 }} />
                  <CardioIndicator level={cardioLev} />
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="excard-header__right">
          {showDurationInput && onDurationChange ? (
            <div className="excard-header__duration" onClick={(e) => e.stopPropagation()}>
              <Timer className="excard-header__duration-icon" style={{ fontSize: 16 }} />
              <DurationMinutesSecondsInput
                className="dms-input--compact"
                aria-label={tr("duration") || "Duration"}

                value={duration}
                onChange={(next) =>
                  onDurationChange(
                    { target: { value: next } },
                    { ...exercise, duration: next, te_duration: next }
                  )
                }
              />
            </div>
          ) : (
            <span className="excard-header__duration-unit" style={{ marginRight: 0 }}>
              {formatDurationLabel(duration) ||
                `${parseDurationMinutes(duration) || duration} ${tr("min")}`}
            </span>
          )}
          {isSingle ? (
            isSelected && (
              <span className="excard-header__select-btn excard-header__select-btn--active">
                <CheckCircle style={{ fontSize: 22 }} />
              </span>
            )
          ) : (
            <button
              type="button"
              className={`excard-header__select-btn ${isSelected ? "excard-header__select-btn--active" : ""}`}
              onClick={(e) => { e.stopPropagation(); onToggle && onToggle(exercise); }}
            >
              {isSelected ? <Remove /> : <Add />}
            </button>
          )}
        </div>
      </div>

      {objective && (
        <div className="excard-section excard-section--objective">
          <SportsScore className="excard-section__icon" />
          <p className="excard-section__text">{objective}</p>
        </div>
      )}

      {themes.length > 0 && (
        <div className="excard-row excard-row--themes">
          <TrackChanges className="excard-row__icon" />
          <div className="excard-row__badges">
            {themes.map((theme, idx) => {
              const tid = theme?.theme_id || theme?.the_id || theme?.th_id;
              const highlighted =
                highlightThemeId != null &&
                String(tid) === String(highlightThemeId);
              return (
                <ThemeBadge
                  key={idx}
                  theme={theme}
                  lng={lang}
                  highlighted={highlighted}
                />
              );
            })}
          </div>
        </div>
      )}

      {skills.length > 0 && (
        <div className="excard-row excard-row--skills">
          <Psychology className="excard-row__icon" />
          <div className="excard-row__badges">
            {skills.map((skill, idx) => (
              <span key={idx} className="excard-badge excard-badge--skill">
                {getLocalized(skill, "skill_name", lang) || (typeof skill === "string" ? skill : "—")}
              </span>
            ))}
          </div>
        </div>
      )}

      {focusList.length > 0 && (
        <div className="excard-section excard-section--focus">
          <TrackChanges className="excard-section__icon" style={{ color: "#e65100" }} />
          <div className="excard-section__text">
            {focusList.map((f, idx) => {
              const focusText =
                f[`focus_description_${lang}`] ||
                f.focus_description_en ||
                f.focus_description_fr ||
                "—";
              return <p key={idx} className="excard-section__focus-item">{focusText}</p>;
            })}
          </div>
        </div>
      )}

      {hasInstruction && (
        <div className="excard-expand">
          <button
            type="button"
            className="excard-expand__toggle"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            {tr("instruction")}
            {expanded ? <ExpandLess style={{ fontSize: 18 }} /> : <ExpandMore style={{ fontSize: 18 }} />}
          </button>
          {expanded && hasInstruction && <p className="excard-expand__content">{instruction}</p>}
        </div>
      )}
    </div>
  );
};
